import { useState } from 'react'
import { AdminShell } from '@/components/admin/AdminShell'
import { SectionHeader } from '@/components/admin/SectionHeader'
import { Fact } from '@/components/admin/Fact'
import { adminSectionHref, visitAdminSection } from '@/components/admin/adminSections'
import { RATING_COLOR, RATING_LABEL } from '@/components/admin/ratingColors'
import { AdminBreadcrumb } from '@/components/admin/user/AdminBreadcrumb'
import { DistributionBars } from '@/components/admin/user/DistributionBars'
import { LearnerWordsModal } from '@/components/admin/user/LearnerWordsModal'
import { StatTile } from '@/components/admin/user/StatTile'
import { shortDate, sinceLabel } from '@/components/admin/user/adminDates'
import { VocabularyBrainView } from '@/components/languages/VocabularyBrainPanel'
import { Button } from '@/components/ui/Button'
import { fetchAdminUserLanguageStats } from '@/api/admin'
import { useResource } from '@/hooks/useResource'
import { STATUS_LABELS } from '@/utils/vocabularyStatus'
import type { AdminUserLanguageStats } from '@/types/admin'
import type { WordStatus } from '@/types/word'

interface AdminUserLanguageProps {
  user_id: number
  language_id: number
}

const STATUSES: WordStatus[] = ['new', 'learning', 'review', 'mastered']

/** One learner in one language: how much they hold, how strong it is, and how they review. */
export default function AdminUserLanguage({ user_id: userId, language_id: languageId }: AdminUserLanguageProps) {
  const [searching, setSearching] = useState(false)
  const { data: stats, error } = useResource(
    () => fetchAdminUserLanguageStats(userId, languageId).then((res) => res.data.data),
    [userId, languageId],
    'Could not load these stats. Reload to try again.',
  )

  const trail = [{ label: 'Users', href: adminSectionHref('users') }]
  if (stats) trail.push({ label: stats.user.email, href: `/admin/users/${userId}` })

  return (
    <AdminShell active="users" onSelect={visitAdminSection}>
      <SectionHeader
        title={stats?.language.name ?? 'Language'}
        breadcrumb={<AdminBreadcrumb trail={trail} />}
        action={stats && <Button onClick={() => setSearching(true)}>Search words</Button>}
      />

      {error ? (
        <p role="alert" className="font-serif text-sm text-body">{error}</p>
      ) : stats ? (
        <LanguageStats stats={stats} />
      ) : (
        <div data-testid="stats-skeleton" aria-busy="true" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-lg bg-surface-bg" />
          ))}
        </div>
      )}

      {searching && stats && (
        <LearnerWordsModal
          userId={userId}
          languageId={languageId}
          languageName={stats.language.name}
          onClose={() => setSearching(false)}
        />
      )}
    </AdminShell>
  )
}

function LanguageStats({ stats }: { stats: AdminUserLanguageStats }) {
  const { words, reviews } = stats

  return (
    <div className="flex flex-col gap-12">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Words" value={words.total.toLocaleString()} hint="tracked, with their own cards" />
        <StatTile label="Due now" value={words.due.toLocaleString()} hint="cards waiting for review" />
        <StatTile label="Reviews" value={reviews.last_30_days.toLocaleString()} hint="in the last 30 days" />
        <StatTile label="Last active" value={sinceLabel(stats.last_active_at)} hint="reviewed or added something" />
      </dl>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        <StatsBlock title="Strength">
          <DistributionBars
            label="Words by status"
            rows={STATUSES.map((status) => ({
              label: STATUS_LABELS[status],
              count: words.by_status[status],
              color: `var(--color-status-${status}-bg)`,
            }))}
          />
        </StatsBlock>

        <StatsBlock title="Reviews">
          <DistributionBars
            label="Reviews by rating"
            rows={[1, 2, 3, 4].map((rating) => ({
              label: RATING_LABEL[rating],
              count: reviews.ratings[rating] ?? 0,
              color: RATING_COLOR[rating],
            }))}
          />
          <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4">
            <Fact label="All time" value={reviews.total.toLocaleString()} />
            <Fact label="Last reviewed" value={sinceLabel(reviews.last_reviewed_at)} />
          </dl>
        </StatsBlock>
      </div>

      <StatsBlock title="Added">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
          <Fact label="Readings" value={stats.readings_count.toLocaleString()} />
          <Fact label="Own word cards" value={stats.custom_cards.words.toLocaleString()} />
          <Fact label="Phrases" value={stats.custom_cards.phrases.toLocaleString()} />
          <Fact label="Enrolled" value={stats.enrolled_at ? shortDate(stats.enrolled_at) : 'Not enrolled'} />
        </dl>
      </StatsBlock>

      <VocabularyBrainView
        stats={stats.vocabulary}
        loading={false}
        error={null}
        title="Their vocabulary"
      />
    </div>
  )
}

function StatsBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title}>
      <h3 className="mb-5 font-serif text-lg text-heading">{title}</h3>
      {children}
    </section>
  )
}
